import React, { useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { change, Field, getFormValues, InjectedFormProps, reduxForm } from 'redux-form';
import { useAppDispatch, useAppSelector } from '../../commons/store/hooks';
import { AppDispatch } from '../../commons/store/store';
import { RootState } from '../../commons/reducers/rootReducer';
import {
  closeRepairCostDialog,
  deleteRepairCost,
  removePendingFile,
  submitRepairCost,
  updatePendingFile,
  validateRepairCostForm,
} from './repairCostDucks';
import '../../commons/styles/dropzone.scss';
import './repairCostDialogStyles.scss';
import Input from '../../commons/components/input/Input';
import {
  InvoiceStatus,
  InvoiceStatusLabels,
  Rebillable,
  RebillableLabelButtons,
  RebillingType,
  RebillingTypeLabels,
  REPAIR_COST_FORM_NAME,
  RepairCostFormValues,
  RepairCostType,
} from './type';
import { SelectField } from '../../commons/components/fields/SelectField';
import { RepairCostFileDropzone } from './RepairCostFileDropzone';
import { getRepairCost, selectSecurityContextRoles } from '../../commons/selectors/selectors';
import classNames from 'classnames';
import Textarea from '../../commons/components/input/Textarea';
import SegmentedControl from '../../commons/components/segmented-control/SegmentedControl';
import { REPAIR_COST_INVOICE_REFACT_WRITE, REPAIR_COST_WRITE } from '../../commons/security/userRoles';
import { repairCostToFormInitialValues } from './repairCostUtils';
import { openConfirmDialog } from '../../commons/components/modal/confirmModalDucks';
import DateTimeField from '../../commons/components/input/DateTimeField';
import moment, { Moment } from 'moment/moment';

const RepairCostDialog = ({ handleSubmit }: InjectedFormProps<RepairCostFormValues>) => {
  const dispatch = useAppDispatch();
  const roles = useAppSelector(selectSecurityContextRoles);

  const { dialogOpen, repairId, repairCost, pendingQuoteFile, pendingOrderFile, pendingInvoiceFile } = useSelector(
    (state: RootState) => state.repairCosts,
  );

  const [editMode, setEditMode] = useState<boolean>(!repairCost);
  const isReadonly = !editMode;

  const formValues = useSelector(getFormValues(REPAIR_COST_FORM_NAME)) as RepairCostFormValues;
  const [showQuote, setShowQuote] = useState(!!formValues.quote);
  const [showOrder, setShowOrder] = useState(!!formValues.order);
  const [showInvoice, setShowInvoice] = useState(!!formValues.invoice);

  const handleRebillableChange =
    (_: RepairCostFormValues, update: { rebillable: Rebillable }) => (innerDispatch: AppDispatch) => {
      if (update.rebillable !== 'YES') {
        innerDispatch(change(REPAIR_COST_FORM_NAME, 'rebilling', null));
      }
    };

  const canEditRepairCost = roles.includes(REPAIR_COST_WRITE);
  const isRebillable = formValues?.rebillable === 'YES';
  const isRebillingTypeOthers = isRebillable && formValues?.rebilling?.type === 'OTHERS';

  const rebillingTypeOptions = (Object.keys(RebillingTypeLabels) as RebillingType[]).map((key) => ({
    value: key,
    label: RebillingTypeLabels[key],
  }));

  const invoiceStatusOptions = (Object.keys(InvoiceStatusLabels) as InvoiceStatus[])
    .filter((is) => roles.includes(REPAIR_COST_INVOICE_REFACT_WRITE) || is !== 'PAID') // user can select PAID only if he has REPAIR_COST_INVOICE_REFACT_WRITE role
    .map((key) => ({
      value: key,
      label: InvoiceStatusLabels[key],
    }));

  const handleToggleQuote = () => {
    if (showQuote) {
      setShowQuote(false);
      dispatch(change(REPAIR_COST_FORM_NAME, 'quote', null));
    } else {
      setShowQuote(true);
      dispatch(change(REPAIR_COST_FORM_NAME, 'quote', { number: null, date: null, cost: null }));
    }
  };

  const handleToggleOrder = () => {
    if (showOrder) {
      setShowOrder(false);
      dispatch(change(REPAIR_COST_FORM_NAME, 'order', null));
    } else {
      setShowOrder(true);
      dispatch(change(REPAIR_COST_FORM_NAME, 'order', { number: null, date: null, cost: null }));
    }
  };

  const handleToggleInvoice = () => {
    if (showInvoice) {
      setShowInvoice(false);
      dispatch(change(REPAIR_COST_FORM_NAME, 'invoice', null));
    } else {
      setShowInvoice(true);
      dispatch(change(REPAIR_COST_FORM_NAME, 'invoice', { number: null, date: null, cost: null, status: null }));
    }
  };

  const handleCancel = () => dispatch(closeRepairCostDialog());

  const onSubmit = (values: RepairCostFormValues) => {
    dispatch(submitRepairCost(repairId!, values, pendingQuoteFile, pendingOrderFile, pendingInvoiceFile));
  };

  const renderFileButtonsByCostType = (type: RepairCostType) => {
    return (
      <div className="repair-cost-file-buttons">
        <span
          className="btn btn-icon download"
          onClick={() => window.open(`/api/repair-costs/${formValues.repairCostId}/file/${type}`)}
        />
        {!isReadonly && canEditRepairCost && (
          <span
            className="btn btn-icon remove"
            onClick={() => {
              // remove existing File
              dispatch(change(REPAIR_COST_FORM_NAME, type.toLowerCase() + '.fileName', null));
              dispatch(removePendingFile(type));
            }}
          />
        )}
      </div>
    );
  };

  function pastOrPresent(date: Moment | null) {
    return date != null ? date.isSameOrBefore(moment()) : false;
  }

  const handleDelete = () => {
    dispatch(
      openConfirmDialog({
        title: 'Voulez-vous vraiment supprimer ce coût de réparation ?',
        actionText: 'Supprimer le coût de réparation',
        action: () => deleteRepairCost(repairId!, formValues.repairCostId!),
        actionClass: 'danger',
      }),
    );
  };

  if (!dialogOpen || !repairId) {
    return null;
  }

  return (
    <div className="repair-cost-dialog">
      <div className="dialog-fade" onClick={handleCancel} />
      <div className="dialog-body">
        <h1 className="dialog-title">{repairCost?.id ? 'Coûts de réparation' : 'Ajouter des coûts de réparation'}</h1>

        <form className="dialog-form" onSubmit={handleSubmit(onSubmit)}>
          {/* ---- Devis ---- */}
          <div className="form-section">
            <div className="section-header">
              <h2 className="section-title">Devis</h2>
              {!isReadonly && (
                <button
                  type="button"
                  className={classNames('btn btn-icon', showQuote ? 'delete' : 'plus')}
                  onClick={handleToggleQuote}
                  title={showQuote ? 'Supprimer cet élément' : 'Ajouter cet élément'}
                ></button>
              )}
            </div>
            {(showQuote || (isReadonly && formValues.quote)) && (
              <>
                <Field
                  component={Input}
                  name="quote.number"
                  labelText="Numéro"
                  placeholder={'Numéro'}
                  readOnly={isReadonly}
                  required={showQuote}
                  maxLength={32}
                />
                <DateTimeField
                  className="date-field"
                  name="quote.date"
                  labelText="Date"
                  data={formValues}
                  disabled={isReadonly}
                  isValidDate={pastOrPresent}
                />
                <Field
                  component={Input}
                  name="quote.cost"
                  labelText="Coût"
                  type="number"
                  step="0.01"
                  min="0"
                  unit="€"
                  placeholder="0.00"
                  readOnly={isReadonly}
                  required={showQuote}
                />
                {!isReadonly && !formValues.quote?.fileName && (
                  <RepairCostFileDropzone
                    label="Devis"
                    file={pendingQuoteFile}
                    onDrop={(files) => dispatch(updatePendingFile(files[0] ?? null, 'QUOTE'))}
                    onRemove={() => dispatch(removePendingFile('QUOTE'))}
                  />
                )}
                {formValues.repairCostId && formValues.quote?.fileName && (
                  <div className="repair-cost-file-row">
                    <span className="file-name">{formValues.quote.fileName}</span>
                    {renderFileButtonsByCostType('QUOTE')}
                  </div>
                )}
              </>
            )}
          </div>

          {/* ---- Commande ---- */}
          <div className="form-section">
            <div className="section-header">
              <h2 className="section-title">Commande</h2>
              {!isReadonly && (
                <button
                  type="button"
                  className={classNames('btn btn-icon', showOrder ? 'delete' : 'plus')}
                  onClick={handleToggleOrder}
                  title={showOrder ? 'Supprimer cet élément' : 'Ajouter cet élément'}
                ></button>
              )}
            </div>
            {(showOrder || (isReadonly && formValues.order)) && (
              <>
                <Field
                  component={Input}
                  name="order.number"
                  labelText="Numéro"
                  placeholder="Numéro"
                  readOnly={isReadonly}
                  maxLength={32}
                  required={showOrder}
                />
                <DateTimeField
                  className="date-field"
                  name="order.date"
                  labelText="Date"
                  data={formValues}
                  disabled={isReadonly}
                  isValidDate={pastOrPresent}
                />
                <Field
                  component={Input}
                  name="order.cost"
                  labelText="Coût"
                  type="number"
                  step="0.01"
                  min="0"
                  unit="€"
                  placeholder="0.00"
                  readOnly={isReadonly}
                  required={showOrder}
                />
                {!isReadonly && !formValues.order?.fileName && (
                  <RepairCostFileDropzone
                    label="Commande"
                    file={pendingOrderFile}
                    onDrop={(files) => dispatch(updatePendingFile(files[0] ?? null, 'ORDER'))}
                    onRemove={() => dispatch(removePendingFile('ORDER'))}
                  />
                )}
                {formValues.repairCostId && formValues.order?.fileName && (
                  <div className="repair-cost-file-row">
                    <span className="file-name">{formValues.order.fileName}</span>
                    {renderFileButtonsByCostType('ORDER')}
                  </div>
                )}
              </>
            )}
          </div>

          {/* ---- Facture ---- */}
          <div className="form-section">
            <div className="section-header">
              <h2 className="section-title">Facture</h2>
              {!isReadonly && (
                <button
                  type="button"
                  className={classNames('btn btn-icon', showInvoice ? 'delete' : 'plus')}
                  onClick={handleToggleInvoice}
                  title={showInvoice ? 'Supprimer cet élément' : 'Ajouter cet élément'}
                ></button>
              )}
            </div>
            {(showInvoice || (isReadonly && formValues.invoice)) && (
              <>
                <Field
                  component={Input}
                  name="invoice.number"
                  labelText="Numéro"
                  placeholder="Numéro"
                  readOnly={isReadonly}
                  maxLength={32}
                  required={showInvoice}
                />
                <DateTimeField
                  className="date-field"
                  name="invoice.date"
                  labelText="Date"
                  data={formValues}
                  disabled={isReadonly}
                  isValidDate={pastOrPresent}
                />
                <Field
                  component={Input}
                  name="invoice.cost"
                  labelText="Coût"
                  type="number"
                  step="0.01"
                  min="0"
                  unit="€"
                  placeholder="0.00"
                  readOnly={isReadonly}
                  required={showInvoice}
                />
                <Field
                  component={SelectField}
                  name="invoice.status"
                  labelText="Statut de la facture"
                  options={invoiceStatusOptions}
                  disabled={isReadonly}
                  required={showInvoice}
                />
                {!isReadonly && !formValues.invoice?.fileName && (
                  <RepairCostFileDropzone
                    label="Facture"
                    file={pendingInvoiceFile}
                    onDrop={(files) => dispatch(updatePendingFile(files[0] ?? null, 'INVOICE'))}
                    onRemove={() => dispatch(removePendingFile('INVOICE'))}
                  />
                )}
                {formValues.repairCostId && formValues.invoice?.fileName && (
                  <div className="repair-cost-file-row">
                    <span className="file-name">{formValues.invoice.fileName}</span>
                    {renderFileButtonsByCostType('INVOICE')}
                  </div>
                )}
              </>
            )}
          </div>

          {/* ---- Re-facturation ---- */}
          {roles.includes('REPAIR_COST_INVOICE_REFACT_WRITE') && (
            <div className="form-section">
              <div className="section-header">
                <h2 className="section-title">Re-facturation</h2>
              </div>
              <Field
                component={SegmentedControl}
                name="rebillable"
                id="rebillable"
                labelText="Re-facturable ?"
                buttons={RebillableLabelButtons}
                active={formValues.rebillable}
                disabled={isReadonly}
                data={formValues}
                saveAction={handleRebillableChange}
              />
              {isRebillable && (
                <>
                  <Field
                    component={SelectField}
                    name="rebilling.type"
                    labelText="Type de re-facturation"
                    options={rebillingTypeOptions}
                    disabled={isReadonly}
                    required={isRebillable}
                  />
                  {isRebillable && isRebillingTypeOthers && (
                    <Field
                      component={Input}
                      name="rebilling.precision"
                      labelText="Précision du type"
                      placeholder="Préciser..."
                      maxLength={255}
                      readOnly={isReadonly}
                      required={isRebillingTypeOthers}
                    />
                  )}
                  <Field
                    component={Input}
                    name="rebilling.number"
                    labelText="Numéro de re-facturation"
                    placeholder="Numéro de re-facturation"
                    maxLength={32}
                    readOnly={isReadonly}
                    required={isRebillable}
                  />
                  <DateTimeField
                    className="date-field"
                    name="rebilling.date"
                    labelText="Date de re-facturation"
                    data={formValues}
                    disabled={isReadonly}
                    isValidDate={pastOrPresent}
                  />
                </>
              )}
            </div>
          )}
          {/* ---- Commentaire ---- */}
          <div className="comment">
            <Field
              component={Textarea}
              name="comment"
              labelText="Commentaire"
              placeholder={isReadonly ? '-' : 'Commentaire...'}
              maxLength={255}
              readOnly={isReadonly}
            />
          </div>

          <div className="btn-group">
            {!isReadonly && (
              <button type="submit" className="btn btn-success">
                Valider
              </button>
            )}
            {isReadonly && canEditRepairCost && (
              <>
                <button type="button" className="btn btn-success" onClick={() => setEditMode(!editMode)}>
                  Modifier
                </button>
                <button type="button" className="btn btn-link remove" onClick={() => handleDelete()}>
                  Supprimer
                </button>
              </>
            )}
            <button type="button" className="btn btn-link" onClick={handleCancel}>
              {isReadonly ? 'Fermer' : 'Annuler'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  const repairCost = getRepairCost(state);
  return { initialValues: repairCostToFormInitialValues(repairCost) };
};

const RepairCostDialogForm = reduxForm<RepairCostFormValues>({
  form: REPAIR_COST_FORM_NAME,
  validate: validateRepairCostForm,
  enableReinitialize: true,
})(RepairCostDialog);

export default connect(mapStateToProps)(RepairCostDialogForm);
